<script setup lang="ts">
import type { SoftwareDeviceType, SoftwareHomeDeviceData, SoftwareItem, SoftwarePlatformData } from '~/api/software'
import { formatSoftwareNumber } from '~/utils/software'
import { resolveTradingAppDefaultPlatform } from '~/utils/homeTradingApps'
import starIcon from '~/assets/icon/star.svg?url'

const { localePath, t } = useAppI18n()
const { isRtl } = useRtl()
const props = defineProps<{
  softwareHomeData?: SoftwarePlatformData<SoftwareHomeDeviceData>
  deviceType?: SoftwareDeviceType
}>()
const deviceType = ref<SoftwareDeviceType>(props.deviceType ?? 1)
const activeCategoryIndex = ref(0)
const PAGE_SIZE = 10
const visibleCount = ref(PAGE_SIZE)
const loadMoreRef = ref<HTMLElement | null>(null)
// 等待本批次渲染完成，避免连续滚动事件重复追加。
const isAppending = ref(false)
const availableDeviceTypes = computed(() =>
  ([1, 2] as const).filter((device) => {
    const categories = props.softwareHomeData?.[device === 1 ? 'ios' : 'android']?.curated ?? []
    return categories.some((category) => category.items.length > 0)
  }),
)
const categories = computed(() => props.softwareHomeData?.[deviceType.value === 1 ? 'ios' : 'android']?.curated ?? [])
const activeCategory = computed(
  () => categories.value.find((category) => category.tabIndex === activeCategoryIndex.value) ?? categories.value[0],
)
const selectedCategoryIndex = computed(() => activeCategory.value?.tabIndex ?? 0)
const tools = computed(() => activeCategory.value?.items ?? [])
const toolColumns = computed(() => {
  const visibleTools = tools.value.slice(0, visibleCount.value)
  return [0, 1, 2].map((columnIndex) => visibleTools.filter((_, index) => index % 3 === columnIndex))
})

watch(deviceType, () => {
  activeCategoryIndex.value = categories.value[0]?.tabIndex ?? 0
})

watch(
  availableDeviceTypes,
  (devices) => {
    if (devices.includes(deviceType.value)) return
    deviceType.value = devices[0] ?? deviceType.value
  },
  { immediate: true },
)

watch(
  () => props.deviceType,
  (nextDeviceType) => {
    if (nextDeviceType === 1 || nextDeviceType === 2) deviceType.value = nextDeviceType
  },
)

watch(
  [deviceType, activeCategoryIndex, tools],
  () => {
    visibleCount.value = PAGE_SIZE
  },
  { flush: 'sync' },
)

onMounted(() => {
  const preferredDevice = resolveTradingAppDefaultPlatform(navigator.userAgent)
  if (availableDeviceTypes.value.includes(preferredDevice)) deviceType.value = preferredDevice
  window.addEventListener('scroll', handleLoadMore, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleLoadMore)
})

/** 与参考首页一致：滚动到列表底部后，从已有数据中追加十款。 */
async function handleLoadMore() {
  if (isAppending.value || visibleCount.value >= tools.value.length || !loadMoreRef.value) return
  const rect = loadMoreRef.value.getBoundingClientRect()
  if (rect.top > window.innerHeight || rect.bottom < 0) return
  isAppending.value = true
  visibleCount.value = Math.min(visibleCount.value + PAGE_SIZE, tools.value.length)
  await nextTick()
  isAppending.value = false
}

/** 从交互入口读取平台，避免模板内联修改响应式状态。 */
function handleSelectDevice(event: Event) {
  const nextDevice = Number((event.currentTarget as HTMLElement).dataset.device)
  if (nextDevice === 1 || nextDevice === 2) deviceType.value = nextDevice
}

/** 从交互入口读取接口提供的分类标识。 */
function handleSelectCategory(event: Event) {
  const nextCategoryIndex = Number((event.currentTarget as HTMLElement).dataset.categoryIndex)
  if (categories.value.some((category) => category.tabIndex === nextCategoryIndex)) {
    activeCategoryIndex.value = nextCategoryIndex
  }
}

/** 生成首页软件详情页路径，允许详情字段为空。 */
function buildHomeSoftwareDetailsPath(item: SoftwareItem) {
  const marketId = String(item.marketId ?? '').trim()
  const appBundleId = String(item.appBundleId ?? '').trim()
  const traderCode = String(item.traderCode ?? '').trim()
  const platformSuffix = marketId === '11' ? 2 : 1
  return `/crypto-apps/${marketId}-${appBundleId}-${traderCode}-${platformSuffix}?fromType=1`
}

/** 整数评分补齐一位小数，小数评分保持接口原值展示。 */
function formatSoftwareRating(value: number | string | undefined) {
  const numberValue = Number(value)
  return Number.isInteger(numberValue) ? numberValue.toFixed(1) : String(value ?? '')
}

/** 为 div 交互入口补齐 Enter 与 Space 键盘行为。 */
function handleKeyboardActivate(event: KeyboardEvent) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  ;(event.currentTarget as HTMLElement).click()
}

const categoryTabRefs = ref<Array<HTMLElement | null>>([])

/** 保存每个 Tab 按钮的 DOM 引用，支持键盘焦点循环。 */
function setCategoryTabRef(el: Element | any, index: number) {
  if (el instanceof HTMLElement) {
    categoryTabRefs.value[index] = el
    return
  }
  categoryTabRefs.value[index] = null
}

/** 键盘焦点到达最后一个分类后，按 Tab 从首个分类继续循环。 */
function onCategoryTabKeydown(event: KeyboardEvent, index: number) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    ;(event.currentTarget as HTMLElement).click()
    return
  }

  const total = categories.value.length
  if (total < 2) return

  if (event.key === 'Tab') {
    if (event.shiftKey && index === 0) {
      event.preventDefault()
      categoryTabRefs.value[total - 1]?.focus()
    } else if (!event.shiftKey && index === total - 1) {
      event.preventDefault()
      categoryTabRefs.value[0]?.focus()
    }
  }
}
</script>

<template>
  <section id="curated-crypto-tools" class="w-full text-[#292a2e]">
    <span id="tools" class="block h-0 scroll-mt-[70px]" aria-hidden="true"></span>
    <div role="heading" aria-level="2" class="text-[24px] leading-[32px] font-semibold">
      {{ t('89335') }}
    </div>

    <div class="mt-[20px] w-full">
      <div class="flex h-[44px] items-center border-b-[0.5px] border-b-[rgba(5,21,36,0.06)]">
        <div class="-mx-[12px] flex h-full items-center" role="tablist">
          <div
            v-for="device in availableDeviceTypes"
            :key="device"
            role="tab"
            tabindex="0"
            :data-device="device"
            :aria-selected="deviceType === device"
            class="relative mx-[12px] flex h-full cursor-pointer items-start text-[20px] leading-[28px] whitespace-nowrap"
            :class="
              deviceType === device
                ? 'font-semibold text-[#292a2e]'
                : 'pb-[16px] font-normal text-[#8c8f97] hover:text-[#8c8f97]/80'
            "
            @click="handleSelectDevice"
            @keydown="handleKeyboardActivate"
          >
            {{ t('89336', ['{0}']).replace('{0}', device === 1 ? 'IOS' : 'Android') }}
            <span
              v-if="deviceType === device"
              class="absolute inset-x-[0px] bottom-[0px] mx-auto h-[4px] w-[28px] rounded-[999px] bg-[#292a2e]"
            />
          </div>
        </div>
      </div>

      <div class="relative mt-[8px] h-[60px] overflow-hidden">
        <div class="-mx-[4px] flex h-full items-start overflow-x-auto overflow-y-hidden py-[12px] pl-[2px] rtl:pl-0 rtl:pr-[2px]" role="tablist">
          <button
            v-for="(category, index) in categories"
            :key="category.tabIndex"
            :ref="(el) => setCategoryTabRef(el, index)"
            type="button"
            role="tab"
            tabindex="0"
            :data-category-index="category.tabIndex"
            :aria-selected="selectedCategoryIndex === category.tabIndex"
            class="curated-category-tab relative mx-[4px] box-border flex h-[36px] shrink-0 cursor-pointer items-center overflow-visible rounded-[2px] border-[0.5px] px-[12px] text-[13px] leading-[20px] whitespace-nowrap"
            :class="[
              { 'is-rtl': isRtl, 'is-active': selectedCategoryIndex === category.tabIndex },
              selectedCategoryIndex === category.tabIndex
                ? 'border-[#112D73] bg-[#112D73] font-[500] text-white'
                : 'border-[rgba(11,18,40,0.14)] bg-white font-normal text-[#292a2e] hover:bg-[#f2f3f5]/80 hover:text-[#1d2129]/80',
            ]"
            @click="handleSelectCategory"
            @keydown="onCategoryTabKeydown($event, index)"
          >
            {{ category.tabName }}
          </button>
        </div>
        <div
          aria-hidden="true"
          class="pointer-events-none absolute top-[0px] right-[0px] h-[60px] w-[32px] bg-gradient-to-l from-white to-white/0 rtl:right-auto rtl:left-[0px] rtl:bg-gradient-to-r"
        />
      </div>

      <div v-if="tools.length" class="curated-columns -mx-[10px] mt-[8px] flex min-h-[168px]">
        <div v-for="(column, columnIndex) in toolColumns" :key="columnIndex" class="mx-[10px] min-w-[0px] flex-1">
          <a
            v-for="(item, itemIndex) in column"
            :key="`${item.appBundleId}-${itemIndex}`"
            :href="localePath(buildHomeSoftwareDetailsPath(item))"
            target="_blank"
            rel="noopener noreferrer"
            class="-mx-[6px] flex h-[84px] min-w-[0px] cursor-pointer items-center rounded-[8px] py-[16px] hover:bg-[#F8F8F8]"
          >
            <CommonImage
              :src="item.logo"
              :alt="item.name"
              :width="52"
              :height="52"
              loading="eager"
              class="mx-[6px] box-border shrink-0 rounded-[6px] border border-solid border-[rgba(5,21,36,0.06)]"
            />
            <div class="-my-[2px] flex min-w-[0px] flex-1 flex-col justify-center">
              <div class="my-[2px] truncate text-[16px] leading-[16px] font-[500]" :title="item.name">
                {{ item.name }}
              </div>
              <div class="my-[2px] truncate text-[12px] leading-[16px] font-normal text-[#7d818a]" :title="item.title">
                {{ item.title }}
              </div>
            </div>
            <div
              v-if="Number(item.rating) > 0"
              class="mx-[2px] flex h-[32px] shrink-0 items-center justify-center rounded-[1000px] bg-[#f8f8f8] px-[12px]"
            >
              <CommonImage :src="starIcon" alt="" :width="14" :height="14" loading="eager" class="mx-[2px]" />
              <span class="font-wiki-Medium text-[16px] leading-[24px]">{{ formatSoftwareRating(item.rating) }}</span>
            </div>
          </a>
        </div>
      </div>
      <div v-else class="mt-[8px] flex h-[168px] items-center justify-center text-[13px] leading-[20px] text-[#8c8f97]">
        {{ t('storeHomeEmpty') }}
      </div>
      <div v-if="visibleCount < tools.length" ref="loadMoreRef" aria-hidden="true" class="h-[1px]" />
    </div>
  </section>
</template>

<style scoped lang="less">
.curated-category-tab {
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(17, 45, 115, 0.3);
  }
}

.is-active {
  &:hover:not([aria-disabled='true']) {
    background: linear-gradient(0deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), #112d73;
  }
}
</style>
